import React, { useRef, useState, useEffect } from 'react';
import { Box } from '@mui/material';
import Typography from '@src/new-components/Typography';
import { useTranslation } from 'react-i18next';
import { COLOR } from '@src/styles/color';
import SignaturePad from 'signature_pad';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import CommonButton from '@src/new-components/Button';

const DigitalSignature = ({
  error,
  helperText,
  onSignatureChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  // Handle focus and click events
  const handleCanvasFocus = () => {
    setIsDrawing(true);
  };

  const handleCanvasClick = () => {
    setIsDrawing(true);
  };
  useEffect(() => {
    if (canvasRef.current && !signaturePadRef.current) {
      signaturePadRef.current = new SignaturePad(canvasRef.current, {
        backgroundColor: 'transparent',
        penColor: COLOR.darkGray,
        minWidth: 1,
        maxWidth: 2,
        dotSize: 1,
      });
      // Handle drawing events
      const handleBeginStroke = () => {
        setIsDrawing(true);
      };

      const handleEndStroke = () => {
        setIsDrawing(false);
        if (!signaturePadRef.current.isEmpty()) {
          setHasSignature(true);
          const signatureData = signaturePadRef.current.toDataURL();
          if (onSignatureChange) {
            onSignatureChange(signatureData);
          }
        } else {
          setHasSignature(false);
          if (onSignatureChange) {
            onSignatureChange(null);
          }
        }
      };

      // Add event listeners using the correct API
      signaturePadRef.current.addEventListener(
        'beginStroke',
        handleBeginStroke,
      );
      signaturePadRef.current.addEventListener('endStroke', handleEndStroke);

      canvasRef.current.addEventListener('focus', handleCanvasFocus);
      canvasRef.current.addEventListener('mousedown', handleCanvasClick);
      canvasRef.current.addEventListener('touchstart', handleCanvasClick);
    }

    return () => {
      if (signaturePadRef.current) {
        signaturePadRef.current.removeEventListener('beginStroke');
        signaturePadRef.current.removeEventListener('endStroke');
      }
      if (canvasRef.current) {
        canvasRef.current.removeEventListener('focus', handleCanvasFocus);
        canvasRef.current.removeEventListener('mousedown', handleCanvasClick);
        canvasRef.current.removeEventListener('touchstart', handleCanvasClick);
      }
    };
  }, []);

  const handleClear = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setHasSignature(false);
      setIsDrawing(false);
      if (onSignatureChange) {
        onSignatureChange(null);
      }
    }
  };

  const renderPlaceholder = () => (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 1,
      }}
    >
      {disabled ? (
        <Typography variant="body-14-medium" color={COLOR.black.default}>
          {t('signed')}
        </Typography>
      ) : (
        <Typography variant="body-12-regular" color={COLOR.label2[64]}>
          {t('drawSignatureDesc')}
        </Typography>
      )}
    </Box>
  );

  const renderClearButton = () => (
    <CommonButton
      variant="outlineDelete"
      customStyle={{
        width: '40px',
        height: '40px',
        minWidth: '40px',
        position: 'absolute',
        right: 12,
        top: 12,
        zIndex: 2,
      }}
      onClick={handleClear}
    >
      <DeleteRoundedIcon />
    </CommonButton>
  );
  return (
    <Box className="digital-signature-container">
      <Typography
        variant="body-14-medium"
        className="input-label"
        color={COLOR.darkGray}
      >
        {t('digitalSignature')}
      </Typography>
      <Box
        className={`digital-signature-content ${
          error ? 'error-signature' : ''
        }`}
        aria-invalid={error}
        sx={{
          position: 'relative',
          border: `1px solid ${
            error ? COLOR.functionalRed.default : COLOR.border
          }`,
          borderRadius: '8px',
          overflow: 'hidden',
          '&:hover': {
            borderColor: COLOR.primary,
          },
        }}
      >
        {!disabled && (
          <canvas
            ref={canvasRef}
            width={560}
            height={200}
            style={{
              cursor: 'crosshair',
            }}
          />
        )}

        {/* Placeholder text - only show when not drawing and no signature */}
        {!isDrawing && !hasSignature && renderPlaceholder()}
        {/* Clear button */}
        {hasSignature && renderClearButton()}
      </Box>

      {error && <Typography className="input-error">{helperText}</Typography>}
    </Box>
  );
};

export default DigitalSignature;
